import { useEffect } from 'react';

import type { InvoiceQuerySchema } from '@/_pages/invoices/shared/schemas';

type UseInvoiceDigitalPdfShortcutOptions = {
  activeInvoiceId: string | null;
  invoices: InvoiceQuerySchema[] | undefined;
};

/**
 * ⌘D / Ctrl+D opens the digital invoice PDF for the highlighted list row (when a URL exists).
 * Uses the capture phase + stopImmediatePropagation so the global sidebar ⌘D shortcut does not run
 * when this handler consumes the event.
 * If there is no active row or no PDF URL, the event is left alone (sidebar navigation works).
 * Ignored while focus is in an input, textarea, select, or contenteditable.
 */
export function useInvoiceDigitalPdfShortcut({ activeInvoiceId, invoices }: UseInvoiceDigitalPdfShortcutOptions) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.code !== 'KeyD' || e.altKey) {
        return;
      }

      if (e.repeat) {
        return;
      }

      const el = e.target as HTMLElement | null;
      if (el?.closest?.('input, textarea, select, [contenteditable="true"]')) {
        return;
      }

      const id = activeInvoiceId;
      if (!id || !invoices?.length) {
        return;
      }

      const inv = invoices.find((i) => String(i.id) === id);
      const url = inv?.digital_invoices_pdf?.at(0)?.url?.trim();
      if (!url) {
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activeInvoiceId, invoices]);
}
